import fetchAllBrands from '@dehaat/kisan-app-bl/api/fetchAllBrands';
import { DEFAULT_LANG, StringBoolean } from '@dehaat/kisan-app-bl/constants/common';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckoutPopup from '@/components/CheckoutPopup';
import ArrowButton from '@/components/icons/ArrowButton';
import LazyImage from '@/components/LazyImage';
import NewSearchBox, { InputHandle } from '@/components/NewSearchBox';
import BrandImageName from '@/components/products/BrandImageName';
import ProductCardLoading from '@/components/products/ProductCardLoading';
import ProductResults from '@/components/products/ProductResults';
import ProductVerticalResults from '@/components/products/ProductVerticalResults';
import RecentSearches from '@/components/products/RecentSearches';
import { IS_PREBOOK_FLOW, NAVIGATION_FROM_APP_CODE, PRODUCTS_QUERY_LIMIT, RECENT_PRODUCT_SEARCHES_COUNT, RECENT_PRODUCT_SEARCHES_KEY, VENDOR_ID_COOKIE_NAME } from '@/constants/common';
import { useAppSelector } from '@/hooks/reduxHooks';
import useDebounce from '@/hooks/useDebounce';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import useIsMobile from '@/hooks/useIsMobile';
import useSearchProductsV2 from '@/hooks/useSearchProductsV2';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { WishlistResponse } from '@/hooks/useWishlist';
import agcLogo from '@/images/agc-logo.png';
import dehaatEnglishLogo from '@/images/dehaat-en-logo.webp';
import notFound from '@/images/product-not-found.png';
import ApiResponse from '@/models/ApiResponse';
import { Brand, Product, Product2 } from '@/models/Product';
import { selectCurrentVendorId, selectLatLng } from '@/store/features/locationSlice';
import style from '@/styles/HideScrollbar.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { event as GAEvent } from '@/utils/analytics/googleAnalytics';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { RelevantSearchItem } from '@/utils/api/fetchSearchProduct';
import fetchWishlist from '@/utils/api/fetchWishlist';
import { get } from '@/utils/axios';
import { getCookieValue, getLocalStorageKey, isAgricentralUser, isDigiAcreUser, setLocalStorageKey } from '@/utils/helper';
import transformProductInventorySet from '@/utils/helpers/transformProductInventorySet';
import CartButton from '../components/cart/MyCart';
interface Props {
  item: RelevantSearchItem;
  bgColor?: string;
  borderColor?: string;
  className?: string;
  onClick: () => void;
  description: string;
}
const SearchResultCard = ({
  item,
  onClick,
  bgColor = 'bg-neutral-50',
  borderColor = 'border-neutral-200',
  className = '',
  description = ''
}: Props) => {
  const {
    app_display_name = '',
    image_url
  } = item;
  return <div className={`flex items-center justify-between cursor-pointer rounded-lg p-2 border max-w-md ${bgColor} ${borderColor} ${className}`} onClick={onClick} data-sentry-component="SearchResultCard" data-sentry-source-file="s.tsx">
      <div className="flex items-center gap-3">
        {image_url && <div className="relative w-10 h-10">
            <LazyImage alt={app_display_name} fallBackIcon={<div className="flex justify-center items-center bg-neutral-200 rounded-full w-8 h-8 overflow-hidden">
                  <span className="text-lg">{app_display_name[0]}</span>
                </div>} imageUrl={image_url} className="rounded-full w-10 h-10 object-cover" />
          </div>}

        <div>
          <div className="font-semibold text-neutral-90 text-sm">
            {app_display_name}
          </div>
          <div className="text-[10px] text-neutral-70">{description}</div>
        </div>
      </div>

      <ArrowButton data-sentry-element="ArrowButton" data-sentry-source-file="s.tsx" />
    </div>;
};
const ProductNotFound = ({
  searchString
}: {
  searchString: string;
}) => {
  return <section className="flex flex-col items-center px-6 py-10 text-center" data-sentry-component="ProductNotFound" data-sentry-source-file="s.tsx">
      <Image src={notFound} alt="Product not found illustration" width={260} height={260} className="mb-4" data-sentry-element="Image" data-sentry-source-file="s.tsx" />

      <h2 className="mb-2 font-semibold text-black text-xl">No Result found</h2>

      <p className="max-w-sm text-neutral-80 text-sm">
        We couldn’t find result on “
        <span className="font-medium">{searchString}</span>”. Try searching
        again.
      </p>
    </section>;
};
const SearchInterface = () => {
  const {
    t
  } = useTranslation('main');
  // State to toggle between the Microphone and the Loader
  const router = useRouter();
  const inputRef = useRef<InputHandle | null>(null);
  const {
    query,
    locale,
    push
  } = router;
  const {
    isWindowLoading,
    isMobile
  } = useIsMobile();
  const currentVendorId = useAppSelector(selectCurrentVendorId);
  const latLng = useAppSelector(selectLatLng) || undefined;
  const [brands, setBrands] = useState<Brand[]>([]);
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const [Wishlist, setWishlist] = useState<Product[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isTypeForEvent, setIsTypeForEvent] = useState(false);
  const [isSearchQueryValid, setIsSearchQueryValid] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const isFromAgricentral = isAgricentralUser();
  const [isTyped, setIsTyped] = useState(false);
  const {
    redirect_uri
  } = query;
  const {
    infiniteScrollRef,
    searchedProducts,
    searchingProducts,
    searchQuery,
    releventSearchs
  } = useSearchProductsV2(PRODUCTS_QUERY_LIMIT, true);
  const {
    suggestionList = [],
    brand: suggestedBrand,
    category: suggestedCategory
  } = releventSearchs || {};
  useEffect(() => {
    const recentSearchValue = getLocalStorageKey(RECENT_PRODUCT_SEARCHES_KEY) as Array<string> || [];
    setRecentSearches(recentSearchValue);
  }, []);
  const updateRecentSearches = (recentSearch: string) => {
    setRecentSearches(prevRecentSearches => {
      const filteredSearches = prevRecentSearches.filter(item => item !== recentSearch);
      const newRecentSearches = [recentSearch, ...filteredSearches.slice(0, RECENT_PRODUCT_SEARCHES_COUNT - 1)];
      setLocalStorageKey(RECENT_PRODUCT_SEARCHES_KEY, newRecentSearches);
      return newRecentSearches;
    });
  };
  const debouncedSearch: any = useDebounce((newSearchQuery: string) => {
    // If search is no longer focused/active, ignore pending debounced work
    // if (!isSearchActiveRef.current) return
    if (searchQuery !== newSearchQuery) {
      setShowSuggestions(true);
      setIsTypeForEvent(isTyped);
      setIsSearchQueryValid(true);
      const currentQuery = {
        ...router.query
      };
      console.log('searchText newSearchQuery', newSearchQuery);

      // Always preserve redirect_uri and other existing params; only add/remove 's'
      if (newSearchQuery) {
        GAEvent('search', {
          search_term: newSearchQuery,
          method: isTyped ? 'TypedSearch' : 'VoiceSearch'
        });
        const nextQuery = {
          ...currentQuery,
          s: encodeURIComponent(newSearchQuery)
        };
        router.replace({
          pathname: router.pathname,
          query: nextQuery
        }, undefined, {
          shallow: true
        });
        // updateRecentSearches(newSearchQuery)
      } else {
        // Remove only 's' from query, keep the rest like redirect_uri, so, tab, etc.
        const {
          s: _omit,
          ...rest
        } = currentQuery as Record<string, any>;
        router.replace({
          pathname: router.pathname,
          query: rest
        }, undefined, {
          shallow: true
        });
      }
      setIsTyped(false);
    }
  }, 500);
  const fetchAllBrand = useCallback(async () => {
    const brands = await fetchAllBrands(locale, latLng, currentVendorId);
    setBrands(brands);
    // LatLng and vendorId are tightly coupled values, so it's better to include currentVendorId only in deps[]
    // since currentVendorId changes immediately after latlng changes
    // This avoids fetching same data twice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendorId, locale]);
  const fetchPopularProduct = useCallback(async () => {
    if (!currentVendorId) return;
    try {
      const params = {
        nearby: currentVendorId ? StringBoolean.TRUE : StringBoolean.FALSE,
        getVariants: StringBoolean.TRUE,
        page_size: 9,
        lang: locale || DEFAULT_LANG,
        vendor_id: currentVendorId,
        isprebook: !!getLocalStorageKey(IS_PREBOOK_FLOW)
      };
      const response = await get<ApiResponse<Product2>>(`/hyperlocal/v1/products`, params, undefined);
      const products = response.results.map((product: Product2) => transformProductInventorySet(product));
      setPopularProducts(products);
    } catch {}
  }, [currentVendorId, locale]);
  const fetchWishList = useCallback(async () => {
    const vendorId = getCookieValue(VENDOR_ID_COOKIE_NAME);
    const userId = getCookieValue('user_id');
    const isprebook = !!getLocalStorageKey(IS_PREBOOK_FLOW);
    const formatAllProducts = (products: Product2[]): Product[] => {
      return products.map(product => transformProductInventorySet(product));
    };
    if (!userId || !vendorId) return;
    try {
      const response: WishlistResponse | undefined = await fetchWishlist({
        userId,
        vendorId,
        locale,
        isprebook,
        page: 1,
        pageSize: 20
      });
      if (response?.results?.products) {
        const formattedProducts = formatAllProducts(response.results.products);
        setWishlist(formattedProducts);
      }
    } catch {}
  }, [locale]);
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      await Promise.allSettled([fetchAllBrand(), fetchPopularProduct(), fetchWishList()]);
      setInitialLoading(false);
    };
    loadInitialData();
  }, [fetchAllBrand, fetchPopularProduct, fetchWishList]);
  useEffect(() => {
    if (isSearchQueryValid && searchQuery && searchedProducts.length > 0) {
      const searchProductName = searchedProducts.map(item => item.name);
      const productKeys: Record<string, string> = {};
      searchProductName.slice(0, 10).forEach((name, index) => {
        productKeys[`Product${index + 1}`] = name;
      });
      const eventParam = {
        SearchKey: searchQuery,
        First10Results: searchProductName,
        SearchType: isTypeForEvent ? 'TypedSearch' : 'VoiceSearch',
        ...productKeys
      };
      trackCustomEvent(CustomEventTypes.PRODUCT_SEARCH_INITIATED, JSON.stringify(eventParam));
      setIsTypeForEvent(false);
      setIsSearchQueryValid(false);
    }
  }, [searchedProducts]);
  const handleSearchSelect = useCallback((searchKey: string) => {
    setShowSuggestions(false);
    updateRecentSearches(searchKey);
    inputRef?.current?.setInputValue(searchKey);
  }, []);
  const fromApp = getCookieValue(NAVIGATION_FROM_APP_CODE);
  const {
    getValueForKey
  } = useFeatureFlags();
  const searchSuggestionCount = Number(getValueForKey('search_suggestion_count', '5') || '5');
  return <main className="bg-gray-100 h-screen min-h-screen overflow-auto" data-sentry-component="SearchInterface" data-sentry-source-file="s.tsx">
      <header className="top-0 z-50 sticky flex justify-between bg-white p-4 border-[#E6EBF0] border-b item-center">
        {!isWindowLoading && !isMobile ? isFromAgricentral ? <Link href="/" className="mr-2">
              <Image alt="Dehaat-logo" src={agcLogo} width={200} height={30} />
            </Link> : fromApp ? <div className="flex items-center mr-2 cursor-pointer" onClick={() => {
        push('/');
      }}>
              <Image alt="Dehaat-logo" src={dehaatEnglishLogo} width={95} height={30} />
            </div> : <Link href="/" className="flex-shrink-0 content-center bg-white mr-2">
              <Image alt="Dehaat-logo" src={dehaatEnglishLogo} width={95} height={30} />
            </Link> : null}

        <NewSearchBox ref={inputRef} initialSearchText={searchQuery} placeholder={t('search_placeholder')} onSearch={debouncedSearch} lang={locale || DEFAULT_LANG} onBackClick={() => {
        trackCustomEvent(CustomEventTypes.APP_BACK_BUTTON);
        // Cancel any scheduled debounced navigation immediately
        if (debouncedSearch?.cancel) debouncedSearch.cancel();
        if (redirect_uri) {
          router.replace(redirect_uri as string);
          return;
        }
        router.back();
      }} onFocus={() => {
        window.scrollTo(0, 0);
      }} showBackBtn setIsTyped={setIsTyped} searchingProducts={searchingProducts} recentSearches={recentSearches} suggestionList={['heelo', 'world', 'name']} autoFocus={true} data-sentry-element="NewSearchBox" data-sentry-source-file="s.tsx" />
        {!isWindowLoading && !isMobile && <CartButton />}
      </header>
      <div className="xl:right-[6px] relative m-auto max-w-5xl">
        {searchQuery.length < 1 && recentSearches.length > 0 && <div className="bg-white px-4 pb-3">
            <div className="flex justify-between items-center py-3">
              <h3 className="text-neutral-70 text-sm">{t('recent_search')}</h3>
              <button className="font-nato-semibold text-primary-100 hover:text-primary-120 text-sm" onClick={() => {
            setRecentSearches([]);
            setLocalStorageKey(RECENT_PRODUCT_SEARCHES_KEY, []);
          }}>
                Clear
              </button>
            </div>

            <div className="space-y-2">
              {recentSearches.map((search, index) => <RecentSearches key={index} recentSearch={search} onClick={handleSearchSelect} isRecentSearch />)}
            </div>
          </div>}

        {showSuggestions && searchedProducts.length > 0 && searchQuery.length > 1 && (suggestionList?.length || suggestedCategory || suggestedBrand) && <div className="space-y-2 space-y-2 bg-white p-4 pb-3">
              {suggestionList.filter((_, idx) => searchQuery.length <= 4 || idx < searchSuggestionCount)?.map((search, index) => <RecentSearches key={index} recentSearch={search} onClick={searchKey => {
          trackCustomEvent(CustomEventTypes.USER_CLICKED_ON_SEARCH_SUGGESTIONS, JSON.stringify({
            searchKey: searchQuery,
            clickedSuggestedProductName: searchKey
          }));
          handleSearchSelect(searchKey);
        }} />)}
              {suggestedCategory && suggestedCategory?.slug_info?.slug && <SearchResultCard item={suggestedCategory} onClick={() => {
          const slug = suggestedCategory.slug_info;
          trackCustomEvent(CustomEventTypes.USER_CLICKED_ON_SEARCH_SUGGESTIONS, JSON.stringify({
            clickedSuggestedCategoryName: suggestedCategory.app_display_name,
            clickedSuggestedCategoryId: suggestedCategory.id,
            searchKey: searchQuery
          }));
          if (slug?.slug) router.push(`/products/categories/${slug.slug}`);
        }} bgColor="bg-[#F9F5FF]" borderColor="border-[#E3D7F5]" description="View all products in this category" className="mt-2" />}
              {suggestedBrand && suggestedBrand?.slug_info?.slug && <SearchResultCard item={suggestedBrand} onClick={() => {
          const slug = suggestedBrand.slug_info;
          trackCustomEvent(CustomEventTypes.USER_CLICKED_ON_SEARCH_SUGGESTIONS, JSON.stringify({
            clickedSuggestedBrandName: suggestedBrand.app_display_name,
            searchKey: searchQuery,
            clickedSuggestedBrandId: suggestedBrand.id
          }));
          if (slug?.slug) router.push(`/products/brands/${slug.slug}`);
        }} description="View all products in this brand" borderColor="border-[#E3F5CE]" bgColor="bg-[#F5FFEB]" className="mt-2" />}
            </div>}

        <div className="px-2 py-4 pb-16">
          {searchQuery.length < 3 && <>
              {initialLoading ? <div className={`flex gap-2 p-2 overflow-hidden`}>
                  <ProductCardLoading count={isMobile && !isWindowLoading ? 2 : 8} />
                </div> : popularProducts.length > 0 ? <div className="mb-2">
                  <h2 className="mx-2 text-neutral-70 text-sm">
                    {t('products:popular_products')}
                  </h2>
                  <ProductVerticalResults products={popularProducts} loading={initialLoading} cartEmpty={false} listType={ProductListTypeEnum.SEARCH} pageType="search" />
                </div> : null}
              {!isDigiAcreUser() && <>
                  {initialLoading ? <div className={`flex gap-6 w-full overflow-x-scroll p-2 ${style.scroll}`}>
                      {Array(isMobile && !isWindowLoading ? 4 : 10).fill(null).map((_, index) => <div key={index} className="bg-neutral-30 border border-neutral-10 rounded w-[100px] h-[150px] animate-pulse shrink-0 grow-0"></div>)}
                    </div> : <>
                      {brands.length > 0 ? <div className="mb-2">
                          <h2 className="mx-2 text-neutral-70 text-sm">
                            {t('popular_brands')}
                          </h2>
                          <div className={`flex gap-2 w-full overflow-x-scroll p-2 ${style.scroll}`}>
                            {brands.slice(0, 12).map(brand => <div key={brand.id} className="bg-white p-3 border rounded-lg">
                                <BrandImageName brand={brand} className="m-auto" />
                              </div>)}
                          </div>
                        </div> : null}
                    </>}
                </>}

              {initialLoading ? <div className={`flex gap-2 p-2 overflow-hidden`}>
                  <ProductCardLoading count={isMobile && !isWindowLoading ? 2 : 8} />
                </div> : Wishlist.length > 0 ? <div className="mb-2">
                  <h2 className="mx-2 text-neutral-70 text-sm">
                    {t('common:my_wishlist')}
                  </h2>
                  <ProductVerticalResults products={Wishlist} loading={initialLoading} cartEmpty={false} listType={ProductListTypeEnum.SEARCH} pageType="search" />
                </div> : null}
            </>}

          {searchQuery.length > 2 && <>
              {showSuggestions && searchingProducts && suggestionList?.length === 0 && <div className={`flex flex-col gap-4 w-full overflow-x-scroll px-4 mb-2 ${style.scroll}`}>
                    {Array(5).fill(null).map((_, index) => {
              const isEven = index % 2 === 0;
              const width = isMobile ? isEven ? 250 : 150 : isEven ? 500 : 300;
              return <div key={index} className="bg-neutral-30 border border-neutral-10 rounded h-[15px] animate-pulse shrink-0 grow-0" style={{
                width
              }}></div>;
            })}
                  </div>}
              <div>
                <h2 className="mx-2 text-base">
                  {`Showing results for “${searchQuery}”`}
                </h2>

                <ProductResults products={searchedProducts} loading={searchingProducts} cartEmpty={false} nextLoaderRef={infiniteScrollRef} listType={ProductListTypeEnum.SEARCH} pageType="search" />
                {!searchingProducts && searchedProducts.length === 0 && <ProductNotFound searchString={searchQuery} />}
              </div>
            </>}
        </div>
      </div>
      <CheckoutPopup data-sentry-element="CheckoutPopup" data-sentry-source-file="s.tsx" />
    </main>;
};
export default SearchInterface;